/*
 * SENSESO custom dropdowns.
 * Enhances every native <select> with a styled trigger + listbox while the native
 * select stays in the DOM as the source of truth (form submits, Horizon `on:change`).
 * Opt out with `data-native-select` on the select or any ancestor.
 */

const SKIP = '[multiple], [data-native-select], [data-native-select] select';
const supportsPopover = typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype;
const records = new Map();
let uid = 0;
let scheduled = false;

const CHEVRON =
  '<svg class="senseso-select__chevron" viewBox="0 0 12 12" width="12" height="12" aria-hidden="true" focusable="false"><path d="M2.5 4.5L6 8l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHECK =
  '<svg class="senseso-select__check" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="M3 8.5l3.2 3L13 4.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function isEligible(select) {
  if (select.matches(SKIP)) return false;
  if (select.size > 1) return false;
  return true;
}

function labelFor(select) {
  const aria = select.getAttribute('aria-label');
  if (aria) return aria;
  if (select.id) {
    const label = document.querySelector(`label[for="${CSS.escape(select.id)}"]`);
    if (label) return label.textContent.trim();
  }
  const wrapping = select.closest('label');
  if (wrapping) {
    const clone = wrapping.cloneNode(true);
    clone.querySelectorAll('select, .senseso-select').forEach((node) => node.remove());
    return clone.textContent.trim();
  }
  return '';
}

class SensesoSelect {
  constructor(select) {
    this.select = select;
    this.id = `senseso-select-${++uid}`;
    this.typeahead = '';
    this.typeaheadTimer = 0;
    this.wasOpen = false;

    this.root = document.createElement('span');
    this.root.className = 'senseso-select';

    this.trigger = document.createElement('button');
    this.trigger.type = 'button';
    this.trigger.className = 'senseso-select__trigger';
    this.trigger.setAttribute('role', 'combobox');
    this.trigger.setAttribute('aria-haspopup', 'listbox');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.setAttribute('aria-controls', this.id);
    this.trigger.innerHTML = `<span class="senseso-select__value"></span>${CHEVRON}`;
    this.value = this.trigger.firstElementChild;
    this.root.append(this.trigger);

    this.list = document.createElement('div');
    this.list.id = this.id;
    this.list.className = 'senseso-select__list';
    this.list.setAttribute('role', 'listbox');
    this.list.tabIndex = -1;
    if (supportsPopover) this.list.popover = 'auto';
    else this.list.hidden = true;

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerPointerDown = () => (this.wasOpen = this.isOpen);
    this.onTriggerKeyDown = this.onTriggerKeyDown.bind(this);
    this.onListClick = this.onListClick.bind(this);
    this.onListKeyDown = this.onListKeyDown.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.onSelectChange = () => this.syncSelection();
    this.onSelectFocus = () => this.trigger.focus();
    this.reposition = this.reposition.bind(this);
    this.onOutside = this.onOutside.bind(this);

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('pointerdown', this.onTriggerPointerDown);
    this.trigger.addEventListener('keydown', this.onTriggerKeyDown);
    this.list.addEventListener('click', this.onListClick);
    this.list.addEventListener('keydown', this.onListKeyDown);
    this.list.addEventListener('toggle', this.onToggle);

    this.mount();
  }

  mount() {
    const { select } = this;
    select.after(this.root);
    (select.closest('dialog') || document.body).append(this.list);
    select.addEventListener('change', this.onSelectChange);
    select.addEventListener('input', this.onSelectChange);
    select.addEventListener('focus', this.onSelectFocus);
    this.hideNative();
    this.build();
  }

  hideNative() {
    this.select.setAttribute('aria-hidden', 'true');
    this.select.tabIndex = -1;
  }

  get isMounted() {
    return this.root.isConnected && this.select.nextElementSibling === this.root;
  }

  get isOpen() {
    return supportsPopover ? this.list.matches(':popover-open') : !this.list.hidden;
  }

  build() {
    const { select, list } = this;
    const label = labelFor(select);
    if (label) {
      this.trigger.setAttribute('aria-label', label);
      list.setAttribute('aria-label', label);
    }
    this.trigger.disabled = select.disabled;
    this.root.classList.toggle('senseso-select--disabled', select.disabled);

    list.replaceChildren();
    let index = 0;
    for (const child of select.children) {
      if (child instanceof HTMLOptGroupElement) {
        const group = document.createElement('div');
        group.className = 'senseso-select__group';
        group.setAttribute('role', 'presentation');
        group.textContent = child.label;
        list.append(group);
        for (const option of child.children) list.append(this.renderOption(option, index++));
      } else if (child instanceof HTMLOptionElement) {
        list.append(this.renderOption(child, index++));
      }
    }
    this.syncSelection();
  }

  renderOption(option, index) {
    const item = document.createElement('div');
    item.className = 'senseso-select__option';
    item.id = `${this.id}-${index}`;
    item.setAttribute('role', 'option');
    item.tabIndex = -1;
    item.dataset.index = String(option.index);
    if (option.lang) item.lang = option.lang;
    if (option.disabled) item.setAttribute('aria-disabled', 'true');
    item.innerHTML = `<span class="senseso-select__label"></span>${CHECK}`;
    item.firstElementChild.textContent = option.textContent.trim();
    if (option.hidden) item.hidden = true;
    return item;
  }

  syncSelection() {
    const { select } = this;
    const selected = select.options[select.selectedIndex];
    this.value.textContent = selected ? selected.textContent.trim() : '';
    for (const item of this.options()) {
      item.setAttribute('aria-selected', String(Number(item.dataset.index) === select.selectedIndex));
    }
  }

  options() {
    return [...this.list.querySelectorAll('.senseso-select__option:not([hidden])')];
  }

  enabledOptions() {
    return this.options().filter((item) => item.getAttribute('aria-disabled') !== 'true');
  }

  open(focus = 'selected') {
    if (this.isOpen || this.select.disabled) return;
    this.build();
    this.positionKey = '';
    if (supportsPopover) this.list.showPopover();
    else {
      this.list.hidden = false;
      this.onToggle({ newState: 'open' });
    }
    this.reposition();
    const items = this.enabledOptions();
    const current = this.list.querySelector('[aria-selected="true"]:not([aria-disabled="true"])');
    const target = focus === 'last' ? items.at(-1) : focus === 'first' ? items[0] : current || items[0];
    target?.focus({ preventScroll: true });
    target?.scrollIntoView({ block: 'nearest' });
  }

  close(returnFocus = true) {
    if (!this.isOpen) return;
    if (supportsPopover) this.list.hidePopover();
    else {
      this.list.hidden = true;
      this.onToggle({ newState: 'closed' });
    }
    if (returnFocus) this.trigger.focus({ preventScroll: true });
  }

  onToggle(event) {
    const open = event.newState === 'open';
    this.trigger.setAttribute('aria-expanded', String(open));
    this.root.classList.toggle('senseso-select--open', open);
    cancelAnimationFrame(this.frame);
    if (open) {
      const follow = () => {
        if (!this.isOpen) return;
        this.reposition();
        this.frame = requestAnimationFrame(follow);
      };
      this.frame = requestAnimationFrame(follow);
      window.addEventListener('scroll', this.reposition, { passive: true, capture: true });
      window.addEventListener('resize', this.reposition);
      if (!supportsPopover) document.addEventListener('pointerdown', this.onOutside, true);
    } else {
      window.removeEventListener('scroll', this.reposition, { capture: true });
      window.removeEventListener('resize', this.reposition);
      document.removeEventListener('pointerdown', this.onOutside, true);
    }
  }

  onOutside(event) {
    if (!this.root.contains(event.target) && !this.list.contains(event.target)) this.close(false);
  }

  reposition() {
    if (!this.isOpen) return;
    const rect = this.trigger.getBoundingClientRect();
    const key = `${rect.left}|${rect.top}|${rect.width}|${window.innerWidth}|${window.innerHeight}`;
    if (key === this.positionKey) return;
    this.positionKey = key;
    const gap = 6;
    const edge = 8;
    const style = this.list.style;
    style.minWidth = `${Math.round(rect.width)}px`;
    style.maxHeight = '';
    const listRect = this.list.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - gap - edge;
    const above = rect.top - gap - edge;
    const openUp = listRect.height > below && above > below;
    const available = Math.max(160, openUp ? above : below);
    style.maxHeight = `${Math.floor(available)}px`;
    const height = Math.min(listRect.height, available);
    const top = openUp ? rect.top - gap - height : rect.bottom + gap;
    const rtl = getComputedStyle(this.trigger).direction === 'rtl';
    let left = rtl ? rect.right - listRect.width : rect.left;
    left = Math.min(Math.max(edge, left), window.innerWidth - listRect.width - edge);
    style.top = `${Math.round(top)}px`;
    style.left = `${Math.round(Math.max(edge, left))}px`;
    this.list.classList.toggle('senseso-select__list--up', openUp);
  }

  choose(item) {
    if (!item || item.getAttribute('aria-disabled') === 'true') return;
    const index = Number(item.dataset.index);
    const changed = index !== this.select.selectedIndex;
    this.close();
    if (!changed) return;
    this.select.selectedIndex = index;
    this.syncSelection();
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onTriggerClick() {
    if (this.wasOpen) {
      this.wasOpen = false;
      return;
    }
    this.isOpen ? this.close() : this.open();
  }

  onTriggerKeyDown(event) {
    const keys = { ArrowDown: 'selected', ArrowUp: 'selected', Enter: 'selected', ' ': 'selected', Home: 'first', End: 'last' };
    if (event.key in keys) {
      event.preventDefault();
      event.stopPropagation();
      this.open(keys[event.key]);
    } else if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.stopPropagation();
      const match = this.findByTypeahead(event.key);
      if (match) this.choose(match);
    }
  }

  onListClick(event) {
    this.choose(event.target.closest('.senseso-select__option'));
  }

  onListKeyDown(event) {
    const items = this.enabledOptions();
    const index = items.indexOf(document.activeElement);
    let next = null;
    switch (event.key) {
      case 'ArrowDown':
        next = items[Math.min(items.length - 1, index + 1)];
        break;
      case 'ArrowUp':
        next = items[Math.max(0, index - 1)];
        break;
      case 'Home':
      case 'PageUp':
        next = items[0];
        break;
      case 'End':
      case 'PageDown':
        next = items.at(-1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        event.stopPropagation();
        this.choose(document.activeElement.closest('.senseso-select__option'));
        return;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      case 'Tab':
        this.close();
        return;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          next = this.findByTypeahead(event.key);
        }
    }
    if (next) {
      event.preventDefault();
      event.stopPropagation();
      next.focus({ preventScroll: true });
      next.scrollIntoView({ block: 'nearest' });
    }
  }

  findByTypeahead(char) {
    clearTimeout(this.typeaheadTimer);
    this.typeahead += char.toLowerCase();
    this.typeaheadTimer = setTimeout(() => (this.typeahead = ''), 600);
    return this.enabledOptions().find((item) => item.textContent.trim().toLowerCase().startsWith(this.typeahead));
  }

  destroy() {
    this.close(false);
    this.select.removeEventListener('change', this.onSelectChange);
    this.select.removeEventListener('input', this.onSelectChange);
    this.select.removeEventListener('focus', this.onSelectFocus);
    this.root.remove();
    this.list.remove();
  }
}

function sweep() {
  scheduled = false;
  for (const [select, record] of records) {
    if (!select.isConnected) {
      record.destroy();
      records.delete(select);
    } else if (!record.isMounted || !isEligible(select)) {
      record.destroy();
      records.delete(select);
    }
  }
  document.querySelectorAll('.senseso-select').forEach((node) => {
    if (![...records.values()].some((record) => record.root === node)) node.remove();
  });
  for (const select of document.querySelectorAll('select')) {
    if (!records.has(select) && isEligible(select)) records.set(select, new SensesoSelect(select));
  }
}

function schedule() {
  if (scheduled) return;
  scheduled = true;
  queueMicrotask(sweep);
}

const observer = new MutationObserver((mutations) => {
  const dirty = new Set();
  let structural = false;
  for (const mutation of mutations) {
    const target = mutation.target;
    if (target.closest?.('.senseso-select, .senseso-select__list')) continue;
    const select = target instanceof HTMLSelectElement ? target : target.parentElement?.closest?.('select');
    if (select && records.has(select)) {
      dirty.add(records.get(select));
      continue;
    }
    if (mutation.type === 'childList') {
      const added = [...mutation.addedNodes].some(
        (node) => node.nodeType === 1 && !node.matches('.senseso-select, .senseso-select__list')
      );
      const removed = [...mutation.removedNodes].some((node) => node.nodeType === 1);
      if (added || removed) structural = true;
    }
  }
  dirty.forEach((record) => {
    record.hideNative();
    record.build();
  });
  if (structural) schedule();
});

function start() {
  sweep();
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled', 'selected', 'label', 'hidden'],
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
